import { ChainData, ChainType } from "@0xsquid/squid-types";
import { rpc } from "@stellar/stellar-sdk";
import { AxiosError } from "axios";
import { getClient } from "../../core/client";
import { getTransactionStatus as getBtcTxStatus } from "../../core/connectors/bitcoin/helpers";
import { SendTransactionStatus } from "../../core/types/transaction";
import { XrplTxStatus } from "../../core/types/xrpl";

interface GetTxStatusParams {
  txHash: string;
}

type GetTxStatusResponse = SendTransactionStatus;

interface GetTxStatusEvmParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusEvm({
  chain,
  txHash,
}: GetTxStatusEvmParams): Promise<GetTxStatusResponse> {
  const client = await getClient<ChainType.EVM>(chain);

  const receipt = await client.getTransactionReceipt(txHash);

  if (!receipt) return SendTransactionStatus.ONGOING;

  switch (receipt.status) {
    case 0:
      return SendTransactionStatus.ERROR;
    case 1:
      return SendTransactionStatus.SUCCESS;
  }

  return SendTransactionStatus.ONGOING;
}

interface GetTxStatusCosmosParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusCosmos({
  txHash,
  chain,
}: GetTxStatusCosmosParams): Promise<GetTxStatusResponse> {
  const client = await getClient<ChainType.COSMOS>(chain);

  const tx = await client.getTx(txHash);

  if (!tx) {
    return SendTransactionStatus.ONGOING;
  }

  if (tx.code === 0) {
    return SendTransactionStatus.SUCCESS;
  }

  return SendTransactionStatus.ERROR;
}

interface GetTxStatusSolanaParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusSolana({
  txHash,
  chain,
}: GetTxStatusSolanaParams): Promise<GetTxStatusResponse> {
  const client = await getClient<ChainType.SOLANA>(chain);
  const txStatus = await client.getSignatureStatus(txHash, {
    searchTransactionHistory: true,
  });

  if (!txStatus.value) {
    return SendTransactionStatus.ONGOING;
  }

  if (txStatus.value.err) {
    return SendTransactionStatus.ERROR;
  }

  switch (txStatus.value.confirmationStatus) {
    case "processed":
    case "confirmed":
      return SendTransactionStatus.ONGOING;
    case "finalized":
      return SendTransactionStatus.SUCCESS;
  }

  return SendTransactionStatus.ONGOING;
}

async function getTxStatusBitcoin({
  txHash,
}: GetTxStatusParams): Promise<GetTxStatusResponse> {
  try {
    const response = await getBtcTxStatus(txHash);

    if (response.status.confirmed) {
      return SendTransactionStatus.SUCCESS;
    }

    return SendTransactionStatus.ONGOING;
  } catch (error) {
    if (error instanceof AxiosError) {
      if (/transaction not found/i.test(error.message)) {
        return SendTransactionStatus.ONGOING;
      }
    }

    throw error;
  }
}

interface GetTxStatusSuiParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusSui({
  chain,
  txHash,
}: GetTxStatusSuiParams): Promise<GetTxStatusResponse> {
  if (chain.chainType !== ChainType.SUI) {
    throw new Error(
      `Invalid chain type, expected ${ChainType.SUI}, got ${chain.chainType}`
    );
  }

  const suiClient = await getClient<ChainType.SUI>(chain);

  const txResponse = await suiClient.waitForTransaction({
    digest: txHash,
    options: {
      showEffects: true,
    },
  });

  if (txResponse.effects?.status.error) {
    return SendTransactionStatus.ERROR;
  }

  switch (txResponse.effects?.status.status) {
    case "success":
      return SendTransactionStatus.SUCCESS;
    case "failure":
      return SendTransactionStatus.ERROR;
    default:
      return SendTransactionStatus.ONGOING;
  }
}

interface GetTxStatusXrplParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusXrpl({
  chain,
  txHash,
}: GetTxStatusXrplParams): Promise<GetTxStatusResponse> {
  const xrplClient = await getClient<ChainType.XRPL>(chain);

  const status = await xrplClient.waitForTransaction(txHash, {
    interval: 1_000,
  });

  if (status === XrplTxStatus.SUCCESS) {
    return SendTransactionStatus.SUCCESS;
  }

  return SendTransactionStatus.ERROR;
}

interface GetTxStatusStellarParams extends GetTxStatusParams {
  chain: ChainData;
}

async function getTxStatusStellar({
  chain,
  txHash,
}: GetTxStatusStellarParams): Promise<GetTxStatusResponse> {
  const client = await getClient<ChainType.STELLAR>(chain);
  const status = await client.waitForTransaction(txHash);

  switch (status) {
    case rpc.Api.GetTransactionStatus.SUCCESS:
      return SendTransactionStatus.SUCCESS;
  }
}

export async function getSendTransactionStatus({
  chain,
  txHash,
}: {
  chain: ChainData;
  txHash: string;
}): Promise<SendTransactionStatus> {
  switch (chain.chainType) {
    case ChainType.EVM:
      return getTxStatusEvm({
        txHash,
        chain,
      });

    case ChainType.COSMOS:
      return getTxStatusCosmos({
        txHash,
        chain,
      });

    case ChainType.SOLANA:
      return getTxStatusSolana({
        txHash,
        chain,
      });

    case ChainType.BTC:
      return getTxStatusBitcoin({
        txHash,
      });

    case ChainType.SUI:
      return getTxStatusSui({
        txHash,
        chain,
      });

    case ChainType.XRPL:
      return getTxStatusXrpl({
        txHash,
        chain,
      });

    case ChainType.STELLAR:
      return getTxStatusStellar({
        txHash,
        chain,
      });

    case ChainType.CANTON:
      throw new Error("Not implemented");
  }
}
